import i18next from "i18next";

import {
  Show,
  createEffect,
  createMemo,
  createSignal,
  lazy,
  on,
  onMount,
} from "solid-js";
import { isServer } from "solid-js/web";

import { getGetGameList, postGetGameWinRecord } from "@/api/clientApi/home";
import { getRecentGameList } from "@/api/clientApi/user";
import { GetRealtimeFiatExchangeCurrRates } from "@/api/clientApi/wallet";
import {
  getActivityAllConfig,
  initConfig,
  svrGetFrontCfgV2,
  svrGetSysConfig,
} from "@/api/serverApi";
import { DialogContainer } from "@/components/Dialogs";
import { APP_ID } from "@/constants/constants";
import { useActivity } from "@/hooks/useActivity";
import { useAutoLogin } from "@/hooks/useAutoLogin";
// import { useChart } from "@/hooks/useChart";
import { useConfigUpdate } from "@/hooks/useConfigUpdate";
import { useMessageListener } from "@/hooks/useMessageListener";
import { usePreload } from "@/hooks/usePreload";
import { useReset } from "@/hooks/useReset";
import { useTournament } from "@/hooks/useTournament";
import { useUrlStorage } from "@/hooks/useUrlStorage";
import { useWatch } from "@/hooks/useWatch";
import { getWSSUrl } from "@/lib/apiUtils";
import { updateLang, updateRnToken } from "@/lib/bridge";
import { isMobile } from "@/lib/isMobile";
import {
  ActivityCfgMap,
  addVipData,
  setActivityCfgMap,
  setAllOpenActivity,
  setBonusCategoryList,
} from "@/stores/activityStore";
import { getAllAgentConfig } from "@/stores/affiliateStore";
import {
  getAppConfig,
  setAndroidAppDownloadLinks,
  setComponentListMap,
  // updateConfigVersion,
  setConfigVersion,
  setFrontCfgMap,
  setOriginalCompListMap,
  setVersion,
} from "@/stores/configStore";
import {
  cryptoStore,
  initRealtimeFiatExchangeCurrRates,
} from "@/stores/cryptoStore";
import { deviceInfo } from "@/stores/deviceStore";
import { setGameStore } from "@/stores/gameStore";
import { routerStore } from "@/stores/routerStore";
import { initSysConfigStore } from "@/stores/sysConfigStore";
import {
  checkLoginCallback,
  getUserInfo,
  store,
  userStore,
} from "@/stores/userStore";
import {
  WebSocketConnectStatus,
  connectWebSocket,
  webSocketStore,
} from "@/stores/websocketStore";
import { FlowProps } from "@/types/base";
import { FrontCfgV2Map } from "@/types/config";
import { ActivityCfg } from "@/types/swagger/activity";
import {
  ComponentInfo,
  GameNode,
  GetSysConfigRsp,
} from "@/types/swagger/front";
import {
  type RouteSectionProps,
  createAsync,
  useBeforeLeave,
  useLocation,
} from "@solidjs/router";

// 初始化应用
export function InitApp(props: FlowProps | RouteSectionProps) {
  const initConfigData = createAsync(() => initConfig());
  const { handleEmailLink } = useReset();
  const [loading, setLoading] = createSignal(false);
  const location = useLocation();
  // TODO: 预加组件
  usePreload({
    timmer: 500,
    preloads: [
      lazy(() => import("@/pages/h5/menu")),
      lazy(() => import("@/pages/h5/affiliate")),
      lazy(() => import("@/pages/h5/sports")),
      lazy(() => import("@/pages/h5/casino")),
      lazy(() => import("@/pages/h5/profile")),
      {
        logins: "1",
        router: lazy(() => import("@/pages/h5/login/index")),
      },
      {
        logins: "1",
        router: lazy(() => import("@/pages/h5/register/index")),
      },
    ],
  });

  const appConfig = createMemo(() => getAppConfig());
  useWatch(appConfig, (newVal) => {
    if (newVal.manifest) {
      if (isServer) {
        return;
      }
      const manifestData = appConfig().manifest;
      // 移除已存在的manifest链接
      const existingManifest = document.querySelector('link[rel="manifest"]');
      if (existingManifest) {
        existingManifest.remove();
      }
      const manifest = document.createElement("link");
      manifest.rel = "manifest";
      manifest.crossOrigin = "use-credentials";
      // manifest.href = `/manifest.json?value=${encodeURIComponent(
      //   JSON.stringify(manifestData),
      // )}`;
      const { getStoredParams } = useUrlStorage();
      const url = new URL(getStoredParams().url);

      // const urlParams = new URLSearchParams(window.location.search);
      // const ch = urlParams.get("ch");

      // for (const [key, value] of Object.entries(myPageParams)) {
      //   if (value != null && value !== "") {
      //     params.set(key, String(value));
      //   }
      // }
      const searchParams = url.searchParams;
      searchParams.set("mApp", "1");
      const rewriteManifest = {
        ...manifestData,
        start_url: `/?${searchParams.toString()}`,
        id: String(APP_ID),
      };

      // 依照 client_type 決定 manifest 使用動態 / 靜態
      if (deviceInfo.client_type == 0) {
        manifest.href = `/manifest.json?value=${encodeURIComponent(
          JSON.stringify(rewriteManifest),
        )}`;
      } else {
        manifest.href = `/${__APP_NAME__}/manifest.webmanifest`;
      }

      document.head.appendChild(manifest);
    }
  });

  createEffect(
    on(
      () => [JSON.stringify(initConfigData()), userStore.isLogin] as const,
      () => {
        const config = initConfigData();
        if (config) {
          const { configVersion, pageConfig } = config;
          if (configVersion) {
            setConfigVersion(configVersion);
            setVersion(configVersion.version);
            setAndroidAppDownloadLinks(configVersion.app_links);
          }
          if (pageConfig && pageConfig.list) {
            const componentListMap = pageConfig.list.reduce(
              (acc, item) => {
                acc[item.key] = item.components;
                return acc;
              },
              {} as Record<string, ComponentInfo[]>,
            );
            setOriginalCompListMap(componentListMap);
            setContinueGame(componentListMap);
          }
        }
      },
    ),
  );
  // set继续游戏游戏列表
  const setContinueGame = async (comp: Record<string, ComponentInfo[]>) => {
    const targetKeys = ["home", "casino"] as const;

    // 收集需要异步填充数据的组件
    const targetComponents = targetKeys.flatMap(
      (key) =>
        comp[key]?.filter(
          (item) =>
            (item.type === "game_list" && item.style === 3) ||
            item.type === "winner",
        ) || [],
    );

    const tasks: Promise<void>[] = [];
    // 判断继续游戏是否存在于targetComponents中，如果存在，则获取并填充继续游戏数据
    if (
      targetComponents.some(
        (item) => item.type === "game_list" && item.style === 3,
      ) &&
      userStore.isLogin
    ) {
      tasks.push(
        (async () => {
          try {
            const recentRes = await getRecentGameList(false);
            const recentGids = (recentRes?.recent_gid || []).slice(0, 10);
            if (recentGids.length) {
              const gamesRes = await getGetGameList({
                ids: recentGids.join(","),
                page_size: "10",
                page: "1",
              });
              const list = gamesRes?.list || [];
              // 按原始 ID 顺序重新排序
              const sortedList = recentGids
                .map((id) => list.find((g) => g.game_id === id))
                .filter((g): g is GameNode => !!g);
              setGameStore("continueGameList", sortedList);
              targetComponents.forEach((item) => {
                if (item.type === "game_list" && item.style === 3) {
                  item.games = sortedList;
                }
              });
            }
          } catch (e) {
            console.error("fetch recent games error", e);
          }
        })(),
      );
    }
    // 判断赢钱模块是否存在于targetComponents中，如果存在，则获取并填充赢钱模块数据
    if (targetComponents.some((item) => item.type === "winner")) {
      tasks.push(
        (async () => {
          try {
            const res = await postGetGameWinRecord();
            const list = res?.list || [];
            targetComponents.forEach((item) => {
              if (item.type === "winner") item.games = list;
            });
          } catch (e) {
            console.error("fetch winner list error", e);
          }
        })(),
      );
    }
    if (tasks.length > 0) await Promise.allSettled(tasks);
    // games为空隐藏
    targetKeys.forEach((key) => {
      if (!comp[key]) return;
      comp[key] = comp[key].filter((item) => {
        if (
          item.type === "game_list" &&
          (!item.games || item.games.length === 0)
        ) {
          return false;
        }
        return true;
      });
    });

    setComponentListMap({ ...comp });
  };

  //客户端接管前需要先执行svrGetFrontCfgV2，获取前端配置

  const sysConfig = createAsync(() => svrGetSysConfig());
  createEffect(
    on(
      createMemo(() => JSON.stringify(sysConfig())),
      () => {
        const sysConfigData = sysConfig();
        if (sysConfigData) {
          initSysConfigStore(sysConfigData as GetSysConfigRsp);
        }
      },
    ),
  );
  const frontCfg = createAsync(() => svrGetFrontCfgV2());
  createEffect(
    on(
      createMemo(() => JSON.stringify(frontCfg())),
      () => {
        const frontCfgData = frontCfg();
        if (
          frontCfgData &&
          typeof frontCfgData === "object" &&
          "list" in frontCfgData
        ) {
          const cfg = (frontCfgData as { list: unknown }).list;
          if (Array.isArray(cfg)) {
            const frontCfgMap = cfg.reduce((acc, item) => {
              acc[item.key] = { ...item.data };
              return acc;
            }, {} as FrontCfgV2Map);
            setFrontCfgMap(frontCfgMap);
          }
        }
      },
    ),
  );

  const actConfig = createAsync(() => getActivityAllConfig());
  createEffect(
    on(
      createMemo(() => JSON.stringify(actConfig())),
      () => {
        const config = actConfig();
        if (!config) return;
        const { activityCfg, activityStatus } = config;
        if (activityStatus?.activity_status) {
          setAllOpenActivity(
            Object.keys(activityStatus.activity_status).map(Number),
          );
        }
        if (activityCfg?.activity_cfg_list) {
          Object.values(activityCfg.activity_cfg_list).forEach((el) => {
            addVipData(el.cfg);
          });
        }
        const cfg = activityCfg?.activity_cfg_list;
        const allActCfg = {} as ActivityCfgMap;
        const list = [] as number[];

        if (cfg) {
          Object.values(cfg).forEach((value: ActivityCfg) => {
            if (!allActCfg[value.atype]) {
              allActCfg[value.atype] = value;
              if (value.bonus_category) {
                list.push(value.bonus_category);
              }
            }
          });
        }

        setActivityCfgMap(allActCfg);
        setBonusCategoryList(list);
      },
    ),
  );

  createEffect(
    on(
      () => userStore.isLogin,
      () => {
        updateRnToken(store.token as string);
        useTournament().initTournament();
      },
      {
        defer: true,
      },
    ),
  );

  onMount(() => {
    // 更新rn壳语言
    updateLang(i18next.language);

    useMessageListener();
    // TODO
    // useChart();

    const { getAllRepeatCfgContainBasic } = useActivity();
    const url = getWSSUrl();
    if (webSocketStore.socketStatus == WebSocketConnectStatus.DISCONNECTED) {
      connectWebSocket(url);
    }
    useAutoLogin().autoLogin();
    handleEmailLink();

    // repeat 活动包含基础设置
    getAllRepeatCfgContainBasic();

    const GetRealtimeFiat = async () => {
      const pointTypes = cryptoStore.fiat_currency_config.map(
        (item) => item.point_type,
      );
      if (pointTypes.length) {
        const res = await GetRealtimeFiatExchangeCurrRates({ pointTypes });
        if (res) {
          initRealtimeFiatExchangeCurrRates(res.rates);
        }
      }
    };

    GetRealtimeFiat();

    // 路由切换后：将 scrollTop 设置为 1，维持 iOS Safari 底部栏折叠
    createEffect(() => {
      if (!isMobile() && !isServer) return;
      void `${location.pathname}${location.search}${location.hash}`;
      const apply = () => {
        const el = document.scrollingElement || document.documentElement;
        el.scrollTop = 1;
        window.scrollTo(0, 1);
      };
      requestAnimationFrame(apply);
    });

    // 自动kyc，getUserInfo传true
    getUserInfo()
      .then((res) => {
        if (res) {
          checkLoginCallback(true);
        }
      })
      .catch((err) => {
        checkLoginCallback(false, err);
      })
      .finally(() => setLoading(true));

    const { updateConfigVersion } = useConfigUpdate();
    updateConfigVersion(true); //更新配置版本 获取有效渠道参数

    useTournament().initTournament();
  });

  useWatch(
    () => routerStore.currentFullPath,
    () => {
      const urlParams = new URLSearchParams(window.location.search);
      // 登入且 url 有 ic 參數，則需要獲取代理活動資料
      if (userStore.isLogin && urlParams.get("ic")) {
        getAllAgentConfig();
      }
    },
  );

  useBeforeLeave(() => getUserInfo());

  return (
    <Show when={loading()} fallback={<></>}>
      <DialogContainer />
      {props.children}
    </Show>
  );
}
